import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useNavigationBlocker } from '@frontend/settings-routing';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, ConfirmationModal, useModalControl } from '@frontend/design-system';
import { useColumnEntityReorderStore } from '../providers';
import { usePendoTrackingForDragDrop } from './hooks';

export const ColumnEntityReorderActions = () => {
  const { t } = useTranslation('schedule');
  const { columnReorderHandlers, isReordering, setIsReordering, setIsInReorderMode, resetColumnReorderHandlers } =
    useColumnEntityReorderStore([
      'columnReorderHandlers',
      'isReordering',
      'setIsReordering',
      'setIsInReorderMode',
      'resetColumnReorderHandlers',
    ]);

  const [columnEntityOrder, setColumnEntityOrder] = usePreference('ScheduleCalendarViewColumnOrder');
  const {
    modalProps,
    openModal: openDiscardConfirmationModal,
    closeModal: closeDiscardConfirmationModal,
  } = useModalControl();

  const { proceed, isBlocked, cancel } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (columnReorderHandlers || modalProps.show) {
          openDiscardConfirmationModal();
          return true;
        }

        return false;
      },
      enableBeforeUnload: true,
    },
    [columnReorderHandlers, modalProps.show]
  );

  const trackConfirmEvent = usePendoTrackingForDragDrop({
    eventName: 'schedule_column_order_saved',
  });

  const trackCancelEvent = usePendoTrackingForDragDrop({
    eventName: 'schedule_column_reorder_canceled',
  });

  const onConfirmSave = () => {
    if (columnReorderHandlers) {
      setIsReordering(true);
      const newCompleteOrderMap = Object.values(columnReorderHandlers).reduce(
        (finalOrderMap, handler) => {
          if (handler && handler.confirm && typeof handler.confirm === 'function') {
            const [calendarEntityType, locationId, newOrderIdMap] = handler.confirm();
            finalOrderMap[calendarEntityType] = {
              ...(finalOrderMap[calendarEntityType] ?? {}),
              [locationId]: newOrderIdMap,
            };
          }
          return finalOrderMap;
        },
        { ...columnEntityOrder }
      );
      trackConfirmEvent();
      setColumnEntityOrder(newCompleteOrderMap);
      setIsReordering(false);
      setIsInReorderMode(false);
    }
  };

  const onConfirmCancel = () => {
    trackCancelEvent();
    if (columnReorderHandlers) {
      Object.values(columnReorderHandlers).forEach(
        (handler) => handler && handler.cancel && typeof handler.cancel === 'function' && handler.cancel()
      );
      setIsReordering(false);
      setIsInReorderMode(false);
    } else {
      resetColumnReorderHandlers();
    }
  };

  const handleCancelLayout = () => {
    if (columnReorderHandlers) {
      openDiscardConfirmationModal();
    } else {
      onConfirmCancel();
    }
  };

  const handleConfirmDiscardLayout = () => {
    if (isBlocked) {
      proceed();
    } else {
      onConfirmCancel();
    }
  };

  const handleCloseModal = () => {
    if (isBlocked) {
      cancel();
    }
    closeDiscardConfirmationModal();
  };

  return (
    <>
      <div css={styles.container}>
        <Button onClick={handleCancelLayout} variant='secondary' disabled={isReordering}>
          {t('Cancel')}
        </Button>
        <Button onClick={onConfirmSave} disabled={isReordering || !columnReorderHandlers} loading={isReordering}>
          {t('Save Layout')}
        </Button>
      </div>
      <ConfirmationModal
        {...modalProps}
        title={t('You have unsaved changes')}
        message={t('Are you sure you want to discard the current column layout?')}
        confirmLabel={t('Discard')}
        onConfirm={handleConfirmDiscardLayout}
        onCancel={handleCloseModal}
        destructive
      />
    </>
  );
};

const styles = {
  container: css`
    padding: ${theme.spacing(1)};
    display: flex;
    gap: ${theme.spacing(1)};
    justify-content: flex-end;
    border-bottom: 1px solid ${theme.colors.neutral20};
  `,
};
